import { Component, type ErrorInfo, type ReactNode } from 'react';
import { posthogCaptureException } from '../lib/posthogClient';

interface Props {
  children: ReactNode;
}
interface State {
  hasError: boolean;
}

/**
 * Top-level React error boundary. React *render* crashes never reach window.onerror, so
 * PostHog's autocapture misses them — this forwards them via posthogCaptureException()
 * (no-op until analytics consent) and shows a minimal recovery screen instead of a blank
 * white page. Scoped at the app root; a thrown render error in any route lands here.
 */
export class ErrorBoundary extends Component<Props, State> {
  override state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    posthogCaptureException(error, {
      $exception_source: 'react-error-boundary',
      component_stack: info.componentStack,
    });
  }

  override render(): ReactNode {
    if (!this.state.hasError) return this.props.children;
    return (
      <div className="grid min-h-screen place-items-center bg-bg px-6 text-center">
        <div className="max-w-md">
          <h1 className="font-display text-2xl font-bold text-txt">Something went wrong</h1>
          <p className="mt-2 text-[13.5px] text-muted">
            An unexpected error interrupted the page. Reloading usually fixes it — if it
            keeps happening, our team has been notified.
          </p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="mt-5 rounded-md bg-accent px-4 py-2 text-[13px] font-semibold text-[#04141d] hover:bg-accentdeep"
          >
            Reload
          </button>
        </div>
      </div>
    );
  }
}
